/**
 * Deal Detail View
 *
 * Displays deal information with tabs for column and Propcode mappings.
 */

import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, FileText, Target, Columns, Copy } from "lucide-react";
import { Card, Loader, Button, Badge } from "@/ui/atoms";
import { EmptyState, SearchableSelect, type TSearchableOption } from "@/ui/molecules";
import type {
    TApiUser,
    TUpdateDealRequest,
    TApiOTBInfoResponse,
    TApiOTBUploadResponse,
} from "@/apis/proda_finance/types";
import { OrganismPropcodeVersions } from "@/ui/organisms/OrganismPropcodeVersions";
import {
    OrganismColumnMappings,
    type TColumnMappingView,
    type TColumnMappingActions,
} from "@/modules/rev_apac/partners/columnMappings";
import { OrganismFilteringRules, type TFilteringRule } from "@/ui/organisms/OrganismFilteringRules";
import { routerPaths, useOpenFileDetail } from "@/modules/rev_apac/router";
import type { TDeal, TAppStore } from "@/store";
import { cn } from "@/utils/utils";
import { OTBUploadArea } from "./OTBUploadArea";
import { DealInfoForm } from "./DealInfoForm";
import { downloadPropcodeMappingWorkbook } from "../../propcodeMapping/buildPropcodeMappingFile";
import {
    DEFAULT_PROPCODE_KIND,
    PROPCODE_KINDS,
    propcodeKindLabel,
} from "../../propcodeMapping/constants";

/**
 * Business Partner basic info
 */
export type TDealBusinessPartnerInfo = {
    id: string;
    name: string;
    code: string;
};

/**
 * CoA Mapping type
 */
export type TCoAMappingView = {
    id: string;
    thirdAccountCode: string;
    thirdAccountName: string;
    yardiAccountCode: string | null;
    yardiAccountName: string | null;
    level: "A" | "P" | "C";
    isInForce: boolean;
    isActive: boolean;
};

/**
 * Propcode Mapping type
 */
export type TPropcodeMappingView = {
    id: string;
    kind: string;
    sourcePropcode: string;
    targetPropcode: string;
    isActive: boolean;
};

type TTabId = "overview" | "otb" | "propcode" | "tb-column-mappings" | "gl-column-mappings";

const tabs: { id: TTabId; label: string; icon: typeof FileText }[] = [
    { id: "overview", label: "Deal Info", icon: FileText },
    { id: "tb-column-mappings", label: "TB Column Mappings", icon: Columns },
    { id: "gl-column-mappings", label: "GL Column Mappings", icon: Columns },
    { id: "propcode", label: "Propcode Mappings", icon: Target },
];

export type TViewDealDetailParams = {
    store: TAppStore;
    useCases: () => {
        onMount: (dealId: string, bpId: string) => void;
        onUnmount: () => void;
        useDealState: () => {
            data: TDeal | null;
            loading: boolean;
            error: string | null;
        };
        useBusinessPartner: () => {
            data: TDealBusinessPartnerInfo | null;
            loading: boolean;
        };
        usePropcodeMappings: () => {
            data: TPropcodeMappingView[] | null;
            loading: boolean;
        };
        /**
         * Server-side search over the company's active master property codes of one
         * kind. Lazy (per keystroke, debounced by `SearchableSelect`) rather than a
         * preloaded list, so every master code is reachable — not just the first page.
         */
        searchMasterPropertyCodes: (query: string, kind: string) => Promise<TSearchableOption[]>;
        propcodeVersionActions: {
            fetchVersions: (dealId: string) => Promise<void>;
            uploadVersion: (dealId: string, file: File, description?: string) => Promise<unknown>;
            createSnapshot: (dealId: string, description?: string) => Promise<unknown>;
            restoreVersion: (dealId: string, versionId: string) => Promise<unknown>;
        };
        onCreatePropcodeMapping: (
            dealId: string,
            data: {
                source_property_code: string;
                target_property_code: string;
                kind: string;
            },
        ) => Promise<void>;
        onUpdatePropcodeMapping: (
            dealId: string,
            mappingId: string,
            data: {
                kind?: string;
                source_property_code?: string;
                target_property_code?: string;
                is_active?: boolean;
            },
        ) => Promise<void>;
        onDeletePropcodeMapping: (dealId: string, mappingId: string) => Promise<void>;
        useColumnMappings: (fileType: "tb" | "gl") => {
            data: TColumnMappingView[] | null;
            loading: boolean;
        };
        columnMappingActions: TColumnMappingActions;
        onUpdateDeal: (dealId: string, data: TUpdateDealRequest) => Promise<void>;
        onUpdateFilteringRules: (
            dealId: string,
            fileType: "tb" | "gl",
            rules: TFilteringRule[],
        ) => Promise<TFilteringRule[]>;
        useUsers: () => {
            data: TApiUser[] | null;
            loading: boolean;
        };
        useOTBInfo: () => {
            data: TApiOTBInfoResponse | null;
            loading: boolean;
        };
        onUploadOTB: (dealId: string, file: File) => Promise<TApiOTBUploadResponse>;
        onRefreshOTBInfo: (dealId: string) => Promise<void>;
        // Permission flags
        canEditDeal?: boolean;
        canEditDealName?: boolean;
        canViewColumns?: boolean;
        canEditTbColumns?: boolean;
        canEditGlColumns?: boolean;
        canUploadOtb?: boolean;
        /** Opens the create wizard prefilled from this deal (needs deal_create). */
        canCopyDeal?: boolean;
        canViewPropcode?: boolean;
        canUploadPropcode?: boolean;
        canSnapshotPropcode?: boolean;
        canRestorePropcode?: boolean;
        canEditPropcode?: boolean;
        canDeletePropcode?: boolean;
    };
};

export type TViewDealDetailProps = Record<string, never>;

const kindColors: Record<string, string> = {
    investment: "bg-blue-100 text-blue-700",
    entity: "bg-green-100 text-green-700",
    asset: "bg-purple-100 text-purple-700",
};

const kindSelectClass = "border rounded px-1 py-1 text-xs";

const KindOptions = () => (
    <>
        {PROPCODE_KINDS.map((k) => (
            <option key={k.value} value={k.value}>
                {k.label}
            </option>
        ))}
    </>
);

export const createViewDealDetail = ({ store, useCases }: TViewDealDetailParams) => {
    return function ViewDealDetail() {
        const { bpId, dealId } = useParams<{ bpId: string; dealId: string }>();
        const navigate = useNavigate();
        const openFileDetail = useOpenFileDetail();
        const {
            onMount,
            onUnmount,
            useDealState,
            useBusinessPartner,
            usePropcodeMappings,
            searchMasterPropertyCodes,
            propcodeVersionActions,
            onCreatePropcodeMapping,
            onUpdatePropcodeMapping,
            onDeletePropcodeMapping,
            useColumnMappings,
            columnMappingActions,
            onUpdateDeal,
            onUpdateFilteringRules,
            useUsers,
            useOTBInfo,
            onUploadOTB,
            onRefreshOTBInfo,
            canEditDeal = true,
            canEditDealName = false,
            canViewColumns = true,
            canEditTbColumns = true,
            canEditGlColumns = true,
            canUploadOtb = true,
            canCopyDeal = false,
            canViewPropcode = true,
            canUploadPropcode = true,
            canSnapshotPropcode = true,
            canRestorePropcode = true,
            canEditPropcode = true,
            canDeletePropcode = true,
        } = useCases();

        const dealState = useDealState();
        const businessPartner = useBusinessPartner();
        const usersState = useUsers();
        const propcodeMappings = usePropcodeMappings();
        const tbColumnMappings = useColumnMappings("tb");
        const glColumnMappings = useColumnMappings("gl");
        const otbInfo = useOTBInfo();

        // Filtering rules state (edit detail). Saving / error / saved are keyed by file
        // type so a GL failure does not render on the TB tab and vice versa.
        const [tbFilteringRules, setTbFilteringRules] = useState<TFilteringRule[]>([]);
        const [glFilteringRules, setGlFilteringRules] = useState<TFilteringRule[]>([]);
        const [filteringRulesSavingFor, setFilteringRulesSavingFor] = useState<"tb" | "gl" | null>(
            null,
        );
        const [filteringRulesSaveErrors, setFilteringRulesSaveErrors] = useState<
            Partial<Record<"tb" | "gl", string>>
        >({});
        const [filteringRulesSavedFor, setFilteringRulesSavedFor] = useState<"tb" | "gl" | null>(
            null,
        );
        const [glTypeSaving, setGlTypeSaving] = useState(false);
        const [glTypeSaveError, setGlTypeSaveError] = useState<string | null>(null);

        const [activeTab, setActiveTab] = useState<TTabId>("overview");
        const [isCreatingPropcode, setIsCreatingPropcode] = useState(false);
        const [createPropcodeDraft, setCreatePropcodeDraft] = useState({
            sourcePropcode: "",
            targetPropcode: "",
            kind: DEFAULT_PROPCODE_KIND as string,
        });
        const [createPropcodeError, setCreatePropcodeError] = useState<string | null>(null);
        const [editingPropcodeId, setEditingPropcodeId] = useState<string | null>(null);
        const [editingPropcodeDraft, setEditingPropcodeDraft] = useState<{
            kind: string;
            sourcePropcode: string;
            targetPropcode: string;
        } | null>(null);
        const [deletingPropcodeId, setDeletingPropcodeId] = useState<string | null>(null);
        const [propcodeEditError, setPropcodeEditError] = useState<string | null>(null);

        useEffect(() => {
            setActiveTab("overview");
            if (dealId && bpId) {
                void onMount(dealId, bpId);
            }
            return onUnmount;
        }, [dealId, bpId, onMount, onUnmount]);

        // Sync filtering rules from deal state when the deal identity changes
        useEffect(() => {
            if (dealState.data) {
                setTbFilteringRules(dealState.data.tbFilteringRules ?? []);
                setGlFilteringRules(dealState.data.glFilteringRules ?? []);
            }
        }, [dealState.data?.id]);

        const handleBackToDeals = useCallback(() => {
            if (bpId) {
                navigate(routerPaths.admin.businessPartnerDeals(bpId));
            }
        }, [bpId, navigate]);

        // Loading state
        if (dealState.loading || businessPartner.loading) {
            return (
                <div className="flex items-center justify-center py-12">
                    <Loader size="lg" />
                </div>
            );
        }

        // Error state
        if (dealState.error) {
            return (
                <div className="space-y-6">
                    <div className="text-center py-8">
                        <p className="text-destructive">{dealState.error}</p>
                        <Button variant="outline" onClick={handleBackToDeals} className="mt-4">
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Deals
                        </Button>
                    </div>
                </div>
            );
        }

        const deal = dealState.data;
        if (!deal) {
            return (
                <EmptyState
                    variant="error"
                    title="Deal not found"
                    description="The requested deal could not be found."
                    action={{ label: "Back to Deals", onClick: handleBackToDeals }}
                />
            );
        }

        const dealFileTypeLabels: Record<string, string> = {
            tb_and_gl: "TB and GL",
            tb_only: "TB Only",
        };

        // Revantage APAC deals send their output to Yardi: Data Platform output is dormant.
        const finalDestinationOptions: { value: string; label: string }[] = [
            { value: "yardi", label: "ERP" },
        ];

        const formatStartPeriod = (startPeriod: string | null): string => {
            if (!startPeriod) return "Not set";
            const date = new Date(startPeriod);
            const months = [
                "January",
                "February",
                "March",
                "April",
                "May",
                "June",
                "July",
                "August",
                "September",
                "October",
                "November",
                "December",
            ];
            return `${months[date.getMonth()]} ${date.getFullYear()}`;
        };

        const renderOverviewTab = () => (
            <DealInfoForm
                deal={deal}
                users={usersState.data ?? []}
                usersLoading={usersState.loading}
                finalDestinationOptions={finalDestinationOptions}
                dealFileTypeLabels={dealFileTypeLabels}
                formatStartPeriod={formatStartPeriod}
                canEdit={canEditDeal}
                canEditName={canEditDealName}
                onSave={async (data) => {
                    if (!dealId) return;
                    await onUpdateDeal(dealId, data);
                }}
            />
        );

        /**
         * Seed for a Propcode dropdown. A stored mapping holds only the code, so the
         * code doubles as the resting label (as the file-review propcode fields do);
         * search results carry the fuller `CODE - Description` label.
         */
        const propcodeSeed = (code: string): TSearchableOption | null =>
            code ? { value: code, label: code } : null;

        const handleDownloadPropcodes = () => {
            if (!propcodeMappings.data || propcodeMappings.data.length === 0) return;
            // .xlsx in upload format (one sheet per kind) so the download can be edited
            // and re-uploaded directly.
            downloadPropcodeMappingWorkbook(
                `propcode-mappings-${deal.code}`,
                propcodeMappings.data.map((m) => ({
                    kind: m.kind,
                    source: m.sourcePropcode,
                    target: m.targetPropcode,
                })),
            );
        };

        const handleCreatePropcode = async () => {
            if (!dealId) return;
            setCreatePropcodeError(null);
            try {
                await onCreatePropcodeMapping(dealId, {
                    source_property_code: createPropcodeDraft.sourcePropcode,
                    target_property_code: createPropcodeDraft.targetPropcode,
                    kind: createPropcodeDraft.kind,
                });
                if (bpId) onMount(dealId, bpId);
                setIsCreatingPropcode(false);
                setCreatePropcodeDraft({
                    sourcePropcode: "",
                    targetPropcode: "",
                    kind: DEFAULT_PROPCODE_KIND,
                });
            } catch (err) {
                const message =
                    err instanceof Error ? err.message : "Failed to create propcode mapping";
                setCreatePropcodeError(message);
            }
        };

        const renderPropcodeTab = () => (
            <div className="space-y-6">
                {/* Version Management */}
                {dealId && propcodeVersionActions && (
                    <OrganismPropcodeVersions
                        dealId={dealId}
                        store={store}
                        propcodeVersionActions={propcodeVersionActions}
                        onVersionChange={() => {
                            if (dealId && bpId) {
                                onMount(dealId, bpId);
                            }
                        }}
                        canUpload={canUploadPropcode}
                        canSnapshot={canSnapshotPropcode}
                        canRestore={canRestorePropcode}
                    />
                )}

                {/* Error messages */}
                {propcodeEditError && (
                    <div
                        className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                        role="alert"
                    >
                        {propcodeEditError}
                    </div>
                )}
                {createPropcodeError && (
                    <div
                        className="rounded-md bg-destructive/10 border border-destructive/30 px-4 py-3 text-sm text-destructive"
                        role="alert"
                    >
                        {createPropcodeError}
                    </div>
                )}

                {/* Mappings Table */}
                <Card className="p-6">
                    {/* Action buttons */}
                    <div className="flex gap-2 items-center justify-end mb-4">
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={handleDownloadPropcodes}
                            disabled={!propcodeMappings.data || propcodeMappings.data.length === 0}
                        >
                            Download
                        </Button>
                        {canEditPropcode && (
                            <Button
                                size="sm"
                                variant="default"
                                onClick={() => {
                                    setIsCreatingPropcode(true);
                                    setCreatePropcodeError(null);
                                    setCreatePropcodeDraft({
                                        sourcePropcode: "",
                                        targetPropcode: "",
                                        kind: DEFAULT_PROPCODE_KIND,
                                    });
                                }}
                                disabled={isCreatingPropcode}
                            >
                                Add new
                            </Button>
                        )}
                    </div>
                    {propcodeMappings.loading ? (
                        <div className="flex justify-center py-8">
                            <Loader size="md" />
                        </div>
                    ) : isCreatingPropcode ||
                      (propcodeMappings.data && propcodeMappings.data.length > 0) ? (
                        <div className="border rounded-lg overflow-hidden">
                            <div className="max-h-96 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted sticky top-0">
                                        <tr>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Kind
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Source Propcode
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Propcode
                                            </th>
                                            <th className="px-4 py-3 text-left font-medium">
                                                Status
                                            </th>
                                            <th className="px-4 py-3 text-right font-medium w-28">
                                                Actions
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y">
                                        {/* Create row */}
                                        {isCreatingPropcode && (
                                            <tr className="bg-blue-50/50">
                                                <td className="px-4 py-3">
                                                    <select
                                                        aria-label="Kind"
                                                        value={createPropcodeDraft.kind}
                                                        onChange={(e) =>
                                                            // A target of the previous kind is not a code of the new one.
                                                            setCreatePropcodeDraft((p) => ({
                                                                ...p,
                                                                kind: e.target.value,
                                                                targetPropcode: "",
                                                            }))
                                                        }
                                                        className={kindSelectClass}
                                                    >
                                                        <KindOptions />
                                                    </select>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <input
                                                        value={createPropcodeDraft.sourcePropcode}
                                                        onChange={(e) =>
                                                            setCreatePropcodeDraft((p) => ({
                                                                ...p,
                                                                sourcePropcode: e.target.value,
                                                            }))
                                                        }
                                                        placeholder="Source propcode"
                                                        className="border rounded px-2 py-1 w-full max-w-[160px] font-mono text-xs"
                                                    />
                                                </td>
                                                <td className="px-4 py-3">
                                                    <div className="w-full max-w-[240px]">
                                                        <SearchableSelect
                                                            key={createPropcodeDraft.kind}
                                                            searchOnOpen
                                                            value={propcodeSeed(
                                                                createPropcodeDraft.targetPropcode,
                                                            )}
                                                            onSearch={(query) =>
                                                                searchMasterPropertyCodes(
                                                                    query,
                                                                    createPropcodeDraft.kind,
                                                                )
                                                            }
                                                            onSelect={(option) =>
                                                                setCreatePropcodeDraft((p) => ({
                                                                    ...p,
                                                                    targetPropcode: option
                                                                        ? option.value
                                                                        : "",
                                                                }))
                                                            }
                                                            placeholder="Search propcode…"
                                                        />
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3">
                                                    <Badge variant="success">Active</Badge>
                                                </td>
                                                <td className="px-4 py-3 text-right">
                                                    <span className="flex gap-1 justify-end">
                                                        <Button
                                                            size="sm"
                                                            variant="default"
                                                            disabled={
                                                                !createPropcodeDraft.sourcePropcode.trim() ||
                                                                !createPropcodeDraft.targetPropcode
                                                            }
                                                            onClick={handleCreatePropcode}
                                                        >
                                                            Save
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="outline"
                                                            onClick={() => {
                                                                setIsCreatingPropcode(false);
                                                                setCreatePropcodeError(null);
                                                            }}
                                                        >
                                                            Cancel
                                                        </Button>
                                                    </span>
                                                </td>
                                            </tr>
                                        )}
                                        {propcodeMappings.data?.map((mapping) => {
                                            const isEditing = editingPropcodeId === mapping.id;
                                            const draft =
                                                isEditing && editingPropcodeDraft
                                                    ? editingPropcodeDraft
                                                    : {
                                                          kind: mapping.kind,
                                                          sourcePropcode: mapping.sourcePropcode,
                                                          targetPropcode: mapping.targetPropcode,
                                                      };
                                            const isDeleting = deletingPropcodeId === mapping.id;
                                            return (
                                                <tr
                                                    key={mapping.id}
                                                    className="hover:bg-muted/50 even:bg-muted/40"
                                                >
                                                    <td className="px-4 py-3">
                                                        {isEditing ? (
                                                            <select
                                                                aria-label="Kind"
                                                                value={draft.kind}
                                                                onChange={(e) =>
                                                                    // A target of the previous kind is not a code of the new one.
                                                                    setEditingPropcodeDraft((p) =>
                                                                        p
                                                                            ? {
                                                                                  ...p,
                                                                                  kind: e.target
                                                                                      .value,
                                                                                  targetPropcode:
                                                                                      "",
                                                                              }
                                                                            : null,
                                                                    )
                                                                }
                                                                className={kindSelectClass}
                                                            >
                                                                <KindOptions />
                                                            </select>
                                                        ) : (
                                                            <span
                                                                className={cn(
                                                                    "inline-flex items-center justify-center px-2 h-6 rounded text-xs font-medium",
                                                                    kindColors[mapping.kind] ||
                                                                        "bg-gray-100 text-gray-700",
                                                                )}
                                                            >
                                                                {propcodeKindLabel(mapping.kind)}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-xs">
                                                        {isEditing ? (
                                                            <input
                                                                value={draft.sourcePropcode}
                                                                onChange={(e) =>
                                                                    setEditingPropcodeDraft((p) =>
                                                                        p
                                                                            ? {
                                                                                  ...p,
                                                                                  sourcePropcode:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : null,
                                                                    )
                                                                }
                                                                className="border rounded px-2 py-1 w-full max-w-[120px] font-mono text-xs"
                                                            />
                                                        ) : (
                                                            mapping.sourcePropcode || (
                                                                <span className="text-muted-foreground italic">
                                                                    (blank)
                                                                </span>
                                                            )
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3 font-mono text-xs">
                                                        {isEditing ? (
                                                            <div className="w-full max-w-[240px]">
                                                                <SearchableSelect
                                                                    key={draft.kind}
                                                                    searchOnOpen
                                                                    value={propcodeSeed(
                                                                        draft.targetPropcode,
                                                                    )}
                                                                    onSearch={(query) =>
                                                                        searchMasterPropertyCodes(
                                                                            query,
                                                                            draft.kind,
                                                                        )
                                                                    }
                                                                    onSelect={(option) =>
                                                                        setEditingPropcodeDraft(
                                                                            (p) =>
                                                                                p
                                                                                    ? {
                                                                                          ...p,
                                                                                          targetPropcode:
                                                                                              option
                                                                                                  ? option.value
                                                                                                  : "",
                                                                                      }
                                                                                    : null,
                                                                        )
                                                                    }
                                                                    placeholder="Search propcode…"
                                                                />
                                                            </div>
                                                        ) : (
                                                            mapping.targetPropcode
                                                        )}
                                                    </td>
                                                    <td className="px-4 py-3">
                                                        <Badge
                                                            variant={
                                                                mapping.isActive
                                                                    ? "success"
                                                                    : "outline"
                                                            }
                                                        >
                                                            {mapping.isActive
                                                                ? "Active"
                                                                : "Inactive"}
                                                        </Badge>
                                                    </td>
                                                    <td className="px-4 py-3 text-right">
                                                        {isEditing ? (
                                                            <span className="flex gap-1 justify-end">
                                                                <Button
                                                                    size="sm"
                                                                    variant="default"
                                                                    disabled={!draft.targetPropcode}
                                                                    onClick={async () => {
                                                                        if (
                                                                            !dealId ||
                                                                            !editingPropcodeDraft
                                                                        )
                                                                            return;
                                                                        setPropcodeEditError(null);
                                                                        try {
                                                                            await onUpdatePropcodeMapping(
                                                                                dealId,
                                                                                mapping.id,
                                                                                {
                                                                                    kind: editingPropcodeDraft.kind,
                                                                                    source_property_code:
                                                                                        editingPropcodeDraft.sourcePropcode,
                                                                                    target_property_code:
                                                                                        editingPropcodeDraft.targetPropcode,
                                                                                },
                                                                            );
                                                                            if (bpId)
                                                                                onMount(
                                                                                    dealId,
                                                                                    bpId,
                                                                                );
                                                                            setEditingPropcodeId(
                                                                                null,
                                                                            );
                                                                            setEditingPropcodeDraft(
                                                                                null,
                                                                            );
                                                                        } catch (err) {
                                                                            const message =
                                                                                err instanceof Error
                                                                                    ? err.message
                                                                                    : "Failed to update propcode mapping";
                                                                            setPropcodeEditError(
                                                                                message,
                                                                            );
                                                                        }
                                                                    }}
                                                                >
                                                                    Save
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() => {
                                                                        setEditingPropcodeId(null);
                                                                        setEditingPropcodeDraft(
                                                                            null,
                                                                        );
                                                                        setPropcodeEditError(null);
                                                                    }}
                                                                >
                                                                    Cancel
                                                                </Button>
                                                            </span>
                                                        ) : isDeleting ? (
                                                            <span className="flex gap-1 justify-end">
                                                                <span className="text-xs text-muted-foreground">
                                                                    Delete?
                                                                </span>
                                                                <Button
                                                                    size="sm"
                                                                    variant="destructive"
                                                                    onClick={async () => {
                                                                        if (!dealId) return;
                                                                        try {
                                                                            await onDeletePropcodeMapping(
                                                                                dealId,
                                                                                mapping.id,
                                                                            );
                                                                            if (bpId)
                                                                                onMount(
                                                                                    dealId,
                                                                                    bpId,
                                                                                );
                                                                            setDeletingPropcodeId(
                                                                                null,
                                                                            );
                                                                        } catch (err) {
                                                                            console.error(err);
                                                                        }
                                                                    }}
                                                                >
                                                                    Yes
                                                                </Button>
                                                                <Button
                                                                    size="sm"
                                                                    variant="outline"
                                                                    onClick={() =>
                                                                        setDeletingPropcodeId(null)
                                                                    }
                                                                >
                                                                    No
                                                                </Button>
                                                            </span>
                                                        ) : (
                                                            <span className="flex gap-1 justify-end">
                                                                {canEditPropcode && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        onClick={() => {
                                                                            setPropcodeEditError(
                                                                                null,
                                                                            );
                                                                            setEditingPropcodeId(
                                                                                mapping.id,
                                                                            );
                                                                            setEditingPropcodeDraft(
                                                                                {
                                                                                    kind: mapping.kind,
                                                                                    sourcePropcode:
                                                                                        mapping.sourcePropcode,
                                                                                    targetPropcode:
                                                                                        mapping.targetPropcode,
                                                                                },
                                                                            );
                                                                        }}
                                                                    >
                                                                        Edit
                                                                    </Button>
                                                                )}
                                                                {canDeletePropcode && (
                                                                    <Button
                                                                        size="sm"
                                                                        variant="ghost"
                                                                        className="text-destructive hover:text-destructive"
                                                                        onClick={() =>
                                                                            setDeletingPropcodeId(
                                                                                mapping.id,
                                                                            )
                                                                        }
                                                                    >
                                                                        Delete
                                                                    </Button>
                                                                )}
                                                            </span>
                                                        )}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : (
                        <p className="text-muted-foreground text-center py-8">
                            No propcode mappings configured for this deal. Upload an Excel file or
                            add a propcode mapping above.
                        </p>
                    )}
                </Card>
            </div>
        );

        const renderColumnMappingsTab = (fileType: "tb" | "gl") => {
            const canEditThisColumn = fileType === "tb" ? canEditTbColumns : canEditGlColumns;
            const mappingsState = fileType === "tb" ? tbColumnMappings : glColumnMappings;
            const filteringRules = fileType === "tb" ? tbFilteringRules : glFilteringRules;
            const setFilteringRules = fileType === "tb" ? setTbFilteringRules : setGlFilteringRules;
            const availableColumns =
                mappingsState.data && mappingsState.data.length > 0
                    ? (mappingsState.data[0]?.availableColumns ?? [])
                    : [];

            const handleChangeGlType = async (newGlType: "mtd" | "ytd") => {
                if (!dealId) return;
                setGlTypeSaving(true);
                setGlTypeSaveError(null);
                try {
                    await onUpdateDeal(dealId, { gl_type: newGlType });
                } catch (err) {
                    setGlTypeSaveError(
                        err instanceof Error ? err.message : "Failed to update GL type",
                    );
                } finally {
                    setGlTypeSaving(false);
                }
            };

            const handleSaveFilteringRules = async () => {
                if (!dealId) return;
                // A rule with no values is rejected by the backend (filtering_value has
                // min_length=1), so catch it here rather than surfacing a raw 422.
                const incomplete = filteringRules.find((r) => r.filteringValue.length === 0);
                if (incomplete) {
                    setFilteringRulesSaveErrors((prev) => ({
                        ...prev,
                        [fileType]:
                            `Rule for column "${incomplete.filteringColumnHeader}" has no values. ` +
                            "Add at least one value or remove the rule.",
                    }));
                    return;
                }
                setFilteringRulesSavingFor(fileType);
                setFilteringRulesSavedFor(null);
                setFilteringRulesSaveErrors((prev) => ({ ...prev, [fileType]: undefined }));
                try {
                    const saved = await onUpdateFilteringRules(dealId, fileType, filteringRules);
                    // Adopt the server's view so the panel cannot drift from what is stored.
                    setFilteringRules(saved);
                    setFilteringRulesSavedFor(fileType);
                } catch (err) {
                    setFilteringRulesSaveErrors((prev) => ({
                        ...prev,
                        [fileType]:
                            err instanceof Error ? err.message : "Failed to save filtering rules",
                    }));
                } finally {
                    setFilteringRulesSavingFor(null);
                }
            };

            return (
                <div className="space-y-6">
                    {fileType === "gl" && (
                        <Card className="p-6">
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <h3 className="text-sm font-medium">GL Type</h3>
                                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                                        Whether the GL file received from the business partner
                                        contains Month-to-Date (MTD) or Year-to-Date (YTD) values.
                                        YTD requires a Period column mapping; for MTD the Period
                                        column is optional. Switch this if the file type received
                                        from the business partner changes.
                                    </p>
                                    {glTypeSaveError && (
                                        <p className="text-sm text-destructive mt-2">
                                            {glTypeSaveError}
                                        </p>
                                    )}
                                </div>
                                <select
                                    value={
                                        deal.glType === "ytd" || deal.glType === "mtd"
                                            ? deal.glType
                                            : ""
                                    }
                                    onChange={(e) =>
                                        void handleChangeGlType(e.target.value as "mtd" | "ytd")
                                    }
                                    disabled={!canEditDeal || glTypeSaving}
                                    className="px-3 py-2 text-sm border rounded-md bg-background shrink-0 disabled:opacity-50"
                                >
                                    <option value="" disabled>
                                        Select GL type...
                                    </option>
                                    <option value="mtd">MTD</option>
                                    <option value="ytd">YTD</option>
                                </select>
                            </div>
                        </Card>
                    )}
                    <OrganismColumnMappings
                        dealId={deal.id}
                        fileType={fileType}
                        mappings={mappingsState.data}
                        loading={mappingsState.loading}
                        actions={columnMappingActions}
                        canEdit={canEditThisColumn}
                        glType={deal.glType}
                    />
                    {availableColumns.length > 0 && (
                        <div className="border rounded-lg p-6 space-y-4">
                            <OrganismFilteringRules
                                fileType={fileType}
                                availableColumns={availableColumns}
                                rules={filteringRules}
                                onChange={setFilteringRules}
                            />
                            {filteringRulesSaveErrors[fileType] && (
                                <p className="text-sm text-destructive">
                                    {filteringRulesSaveErrors[fileType]}
                                </p>
                            )}
                            {canEditThisColumn && (
                                <div className="flex items-center justify-end gap-3">
                                    {filteringRulesSavedFor === fileType && (
                                        <p className="text-sm text-muted-foreground">
                                            Saved. Applies to files uploaded or refreshed from now
                                            on — files already processed keep their current rows.
                                        </p>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => void handleSaveFilteringRules()}
                                        disabled={filteringRulesSavingFor === fileType}
                                        className="px-4 py-2 text-sm font-medium rounded bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                                    >
                                        {filteringRulesSavingFor === fileType
                                            ? "Saving..."
                                            : "Save Filtering Rules"}
                                    </button>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            );
        };

        const renderOTBTab = () => {
            const info = otbInfo.data;

            const statusLabels: Record<string, string> = {
                ready_for_commitment: "Ready for Commitment",
                committed: "Committed",
                rejected: "Rejected",
                under_review: "Under Review",
                under_acceptance: "Under Acceptance",
                uploaded: "Uploaded",
            };

            const statusColors: Record<string, string> = {
                ready_for_commitment: "success",
                committed: "success",
                rejected: "destructive",
                under_review: "warning",
            };

            return (
                <div className="space-y-6">
                    {otbInfo.loading ? (
                        <div className="flex items-center justify-center py-12">
                            <Loader size="lg" />
                        </div>
                    ) : info?.has_otb ? (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                                <div>
                                    <p className="text-sm text-muted-foreground">Status</p>
                                    <Badge
                                        variant={
                                            (statusColors[info.status ?? ""] as
                                                | "success"
                                                | "destructive"
                                                | "warning"
                                                | "outline") ?? "outline"
                                        }
                                    >
                                        {statusLabels[info.status ?? ""] ?? info.status}
                                    </Badge>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Period</p>
                                    <p className="font-medium text-sm">{info.otb_period ?? "—"}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Line Items</p>
                                    <p className="font-medium text-sm">
                                        {info.total_line_items ?? "—"}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Uploaded</p>
                                    <p className="font-medium text-sm">
                                        {info.uploaded_at
                                            ? new Date(info.uploaded_at).toLocaleDateString()
                                            : "—"}
                                    </p>
                                </div>
                            </div>

                            {info.file_id && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => openFileDetail(info.file_id!)}
                                >
                                    <FileText className="h-4 w-4 mr-2" />
                                    View OTB File
                                </Button>
                            )}

                            {info.status === "rejected" && canUploadOtb && (
                                <OTBUploadArea
                                    dealId={dealId!}
                                    onUpload={onUploadOTB}
                                    onSuccess={() => {
                                        if (dealId) void onRefreshOTBInfo(dealId);
                                    }}
                                />
                            )}
                        </div>
                    ) : canUploadOtb ? (
                        <OTBUploadArea
                            dealId={dealId!}
                            onUpload={onUploadOTB}
                            onSuccess={() => {
                                if (dealId) void onRefreshOTBInfo(dealId);
                            }}
                        />
                    ) : (
                        <p className="text-sm text-muted-foreground">No OTB uploaded.</p>
                    )}
                </div>
            );
        };

        const renderActiveTab = () => {
            switch (activeTab) {
                case "overview":
                    return renderOverviewTab();
                case "otb":
                    return renderOTBTab();
                case "tb-column-mappings":
                    return renderColumnMappingsTab("tb");
                case "gl-column-mappings":
                    return renderColumnMappingsTab("gl");
                case "propcode":
                    return renderPropcodeTab();
                default:
                    return renderOverviewTab();
            }
        };

        return (
            <div className="space-y-6">
                {/* Header with breadcrumb */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold">{deal.name}</h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            <button
                                onClick={() => navigate(routerPaths.admin.businessPartners)}
                                className="hover:text-foreground hover:underline"
                            >
                                Business Partners
                            </button>
                            {" > "}
                            <button
                                onClick={handleBackToDeals}
                                className="hover:text-foreground hover:underline"
                            >
                                {businessPartner.data?.name || "Deals"}
                            </button>
                            {" > "}
                            <span>{deal.name}</span>
                        </p>
                    </div>
                    <div className="flex items-center gap-2">
                        {canCopyDeal && (
                            <Button
                                variant="outline"
                                onClick={() =>
                                    navigate(
                                        deal.portfolioCompanyId
                                            ? routerPaths.admin.portfolioCompanyDealCreate(
                                                  deal.portfolioCompanyId,
                                                  deal.id,
                                              )
                                            : routerPaths.admin.businessPartnerDealCreate(
                                                  deal.serviceProviderId ?? bpId ?? "",
                                                  deal.id,
                                              ),
                                    )
                                }
                            >
                                <Copy className="h-4 w-4 mr-2" />
                                Copy deal
                            </Button>
                        )}
                        <Button variant="outline" onClick={handleBackToDeals}>
                            <ArrowLeft className="h-4 w-4 mr-2" />
                            Back to Deals
                        </Button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b">
                    <nav className="flex gap-6" aria-label="Tabs">
                        {[
                            ...tabs,
                            ...(deal.acceptsOtb
                                ? [{ id: "otb" as TTabId, label: "OTB", icon: FileText }]
                                : []),
                        ]
                            .filter((tab) => {
                                if (tab.id === "tb-column-mappings") {
                                    return canViewColumns;
                                }
                                if (tab.id === "gl-column-mappings") {
                                    return canViewColumns && deal.dealFileType === "tb_and_gl";
                                }
                                if (tab.id === "propcode") {
                                    return canViewPropcode;
                                }
                                return true;
                            })
                            .map((tab) => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveTab(tab.id)}
                                    className={cn(
                                        "flex items-center gap-2 py-3 text-sm font-medium border-b-2 -mb-px transition-colors",
                                        activeTab === tab.id
                                            ? "border-primary text-primary"
                                            : "border-transparent text-muted-foreground hover:text-foreground hover:border-muted-foreground",
                                    )}
                                >
                                    <tab.icon className="h-4 w-4" />
                                    {tab.label}
                                </button>
                            ))}
                    </nav>
                </div>

                {/* Tab Content */}
                {renderActiveTab()}
            </div>
        );
    };
};
